import React, { useEffect, useMemo, useState } from 'react';
import { Edit2, RefreshCw } from 'lucide-react';
import { systemSettingsService } from '../../services/api';

interface SystemSetting {
  id: number;
  name: string;
  value: string;
  description: string | null;
  updated_by: number | null;
  created_at: string;
  updated_at: string;
}

const AdminSystemSettings: React.FC = () => {
  const [settings, setSettings] = useState<SystemSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const [searchTerm, setSearchTerm] = useState('');
  const [editingName, setEditingName] = useState<string | null>(null);
  const [editValue, setEditValue] = useState('');
  const [editDescription, setEditDescription] = useState('');

  const loadSettings = async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await systemSettingsService.getAll();
      setSettings(response.data || []);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load system settings');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadSettings();
  }, []);

  const resetMessages = () => {
    setError(null);
    setSuccess(null);
  };

  const filteredSettings = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();

    if (!term) {
      return settings;
    }

    return settings.filter((setting) =>
      setting.name.toLowerCase().includes(term) ||
      (setting.description || '').toLowerCase().includes(term) ||
      setting.value.toLowerCase().includes(term)
    );
  }, [settings, searchTerm]);

  const startEdit = (setting: SystemSetting) => {
    setEditingName(setting.name);
    setEditValue(setting.value);
    setEditDescription(setting.description || '');
    resetMessages();
  };

  const cancelEdit = () => {
    setEditingName(null);
    setEditValue('');
    setEditDescription('');
  };

  const handleUpdate = async (name: string) => {
    resetMessages();

    if (!editValue.trim()) {
      setError('Setting value cannot be empty');
      return;
    }

    try {
      setSaving(true);
      await systemSettingsService.update(name, {
        value: editValue,
        description: editDescription || null
      });
      setSuccess(`System setting "${name}" updated successfully`);
      cancelEdit();
      await loadSettings();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update setting');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="p-6 space-y-6">
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-2xl font-bold text-gray-900">System Settings</h2>
          <p className="text-gray-600">View and update adjustable system variables</p>
        </div>
        <button
          onClick={loadSettings}
          disabled={loading}
          className="inline-flex items-center gap-2 rounded-lg border border-gray-300 px-3 py-2 text-sm font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
        >
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {error && (
        <div className="rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {success && (
        <div className="rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-700">
          {success}
        </div>
      )}

      <div className="rounded-lg bg-white p-4 shadow space-y-4">
        <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
          <h3 className="text-lg font-semibold text-gray-900">Available Settings</h3>
          <input
            type="text"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-blue-500 focus:outline-none"
            placeholder="Search name, value, or description..."
          />
        </div>

        {loading ? (
          <div className="py-8 text-center text-gray-500">Loading system settings...</div>
        ) : filteredSettings.length === 0 ? (
          <div className="py-8 text-center text-gray-500">No system settings found.</div>
        ) : (
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Name</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Value</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Description</th>
                  <th className="px-4 py-3 text-left text-xs font-semibold uppercase tracking-wide text-gray-500">Updated At</th>
                  <th className="px-4 py-3 text-right text-xs font-semibold uppercase tracking-wide text-gray-500">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white">
                {filteredSettings.map((setting) => {
                  const isEditing = editingName === setting.name;

                  return (
                    <tr key={setting.id}>
                      <td className="px-4 py-3 align-top text-sm font-medium text-gray-900">{setting.name}</td>
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <textarea
                            value={editValue}
                            onChange={(e) => setEditValue(e.target.value)}
                            rows={2}
                            className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        ) : (
                          <span className="text-sm text-gray-700 whitespace-pre-wrap">{setting.value}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top">
                        {isEditing ? (
                          <input
                            type="text"
                            value={editDescription}
                            onChange={(e) => setEditDescription(e.target.value)}
                            className="w-full rounded-lg border border-gray-300 px-2 py-1 text-sm focus:border-blue-500 focus:outline-none"
                          />
                        ) : (
                          <span className="text-sm text-gray-600">{setting.description || '-'}</span>
                        )}
                      </td>
                      <td className="px-4 py-3 align-top text-sm text-gray-600">
                        {new Date(setting.updated_at).toLocaleString()}
                      </td>
                      <td className="px-4 py-3 align-top">
                        <div className="flex justify-end gap-2">
                          {isEditing ? (
                            <>
                              <button
                                onClick={() => handleUpdate(setting.name)}
                                disabled={saving}
                                className="rounded-md bg-blue-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-blue-700 disabled:opacity-60"
                              >
                                Save
                              </button>
                              <button
                                onClick={cancelEdit}
                                disabled={saving}
                                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-60"
                              >
                                Cancel
                              </button>
                            </>
                          ) : (
                            <button
                              onClick={() => startEdit(setting)}
                              disabled={saving}
                              className="inline-flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs font-medium text-gray-700 hover:bg-gray-50 disabled:opacity-50"
                            >
                              <Edit2 className="h-3.5 w-3.5" />
                              Edit
                            </button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
};

export default AdminSystemSettings;
